import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogContentText,
    Divider,
    FormControl,
    Grid,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    styled,
    TextField,
    Theme,
    Typography,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import useAxios from '../../../../hooks/useAxios';
import useMessageUsersTextTemplates, { MessageUsersTemplate } from '../../../../hooks/useMessageUsersTextTemplates';
import { OnlyFansSubscriber } from '../../../../hooks/useSubscriber';
import { SettingsContext } from '../../../../store/SettingsContext';
import { UserContext } from '../../../../store/UserContext';
import { objectIdToDate, stripHtml } from '../../../../utils/common';
import DialogAdaptive from '../../../common/DialogAdaptive';
import OnlyFansMessageEditorWithPreview from '../../../common/OnlyFansMessageEditorWithPreview';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import { wrapUsernamesWithLinks } from './MessageUsersFormEditiorPreview';

const StyledContent = styled('div')`
    background-color: rgba(0, 175, 240, 0.12);
    margin-left: auto;
    padding: 0px 12px;
    overflow-wrap: break-word;
    word-break: break-word;
    hyphens: auto;
    text-align: start;
    line-height: 20px;
    box-sizing: border-box;

    p {
        margin: 0;
        margin-bottom: 0.5rem;
        padding: 8px 0;
        // other p styles...
    }

    .m-editor-fc__default {
        color: #000;
    }

    .m-editor-fs__lg {
        font-size: 24px;
    }

    .m-editor-fs__l {
        font-size: 20px;
    }

    .m-editor-fs__r {
        font-size: 16px;
    }

    .m-editor-fs__s {
        font-size: 14px;
    }

    .m-editor-fs__sm {
        font-size: 12px;
    }

    .m-editor-fc__blue-1 {
        color: #00aff0;
    }

    .m-editor-fc__blue-2 {
        color: #1b98e0;
    }

    .m-editor-fc__gray {
        color: #8a96a3;
    }
`;

interface MessagePreviewProps {
    message: string;
    theme: Theme;
}

const MessagePreview = (props: MessagePreviewProps) => {
    const { message, theme } = props;

    return (
        <div
            id="bubble-preview"
            style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: '2px',
                flexWrap: 'wrap',
                font: '16px/1.3334 Roboto,sans-serif',
                color: theme.palette.common.black,
            }}
        >
            <div
                id="bubble-preview-content"
                style={{
                    // paddingLeft: '28px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignContent: 'flex-end',
                    alignItems: 'flex-end',
                    position: 'relative',
                }}
            >
                <div
                    id="bubble-preview-body"
                    style={{
                        maxWidth: '100%',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        id="bubble-preview-text-wrapper"
                        style={{
                            flexDirection: 'row-reverse',
                            display: 'flex',
                            position: 'relative',
                        }}
                    >
                        <StyledContent
                            style={{
                                backgroundColor: 'rgba(0,175,240,.12)',
                                marginLeft: 'auto',
                                padding: '10px 12px',
                                overflowWrap: 'break-word',
                                wordBreak: 'break-word',
                                hyphens: 'auto',
                                textAlign: 'start',
                                lineHeight: '20px',
                                boxSizing: 'border-box',
                            }}
                            dangerouslySetInnerHTML={{
                                __html: message,
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

type MessageUsersFormEditorTemplateLoadDialogProps = {
    subscriber: OnlyFansSubscriber;
    loadTemplateDialogOpen: boolean;
    setLoadTemplateDialogOpen: (loadTemplateDialogOpen: boolean) => void;
    handleLoadTemplate: (template: MessageUsersTemplate) => void;
};

const MessageUsersFormEditorTemplateLoadDialog = (props: MessageUsersFormEditorTemplateLoadDialogProps) => {
    const { subscriber, loadTemplateDialogOpen, setLoadTemplateDialogOpen, handleLoadTemplate } = props;
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();
    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();
    const axios = useAxios();

    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [selectedTemplate, setSelectedTemplate] = useState<MessageUsersTemplate | null>(null);

    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [templateName, setTemplateName] = useState<string>('');
    const [replacementUsername, setReplacementUsername] = useState<string>('');
    const [message, setMessage] = useState<string>('');

    const {
        data: templates,
        isLoading: templatesLoading,
        isFetching: templatesFetching,
        refetch: refetchTemplates,
    } = useMessageUsersTextTemplates();

    // Load the first template by default
    useEffect(() => {
        if (templates && templates.length > 0 && !selectedTemplateId) {
            // Only set if currently empty to avoid overwriting user selection
            if (selectedTemplateId === '') {
                const first = templates[0];

                if (first._id) {
                    setSelectedTemplateId(first._id);
                }
            }
        }

        // When dialog closes, reset the selection
        if (!loadTemplateDialogOpen) {
            setSelectedTemplateId('');
        }
    }, [loadTemplateDialogOpen, selectedTemplateId, templates]);

    // Set the selected template when the ID changes
    useEffect(() => {
        if (templates && selectedTemplateId) {
            const foundTemplate = templates.find((t: MessageUsersTemplate) => t._id === selectedTemplateId);

            if (foundTemplate) {
                setSelectedTemplate(foundTemplate);
            }
        }
    }, [selectedTemplateId, templates]);

    const handleClose = () => {
        setLoadTemplateDialogOpen(false);
    };

    const handleSubmit = () => {
        if (!selectedTemplateId) return;

        const foundTemplate = templates?.find((t: any) => t._id === selectedTemplateId);

        if (!foundTemplate) {
            return;
        }

        handleLoadTemplate(foundTemplate);

        handleClose();
    };

    useEffect(() => {
        if (loadTemplateDialogOpen) {
            refetchTemplates();
        }
    }, [loadTemplateDialogOpen, refetchTemplates]);

    // Update Subscriber's Template
    const handleUpdateSelectedTemplate = () => {
        if (!selectedTemplateId) return;

        const updateTemplate = async (): Promise<any> => {
            if (subscriber && userContext.jwtToken && settingsContext.apiKey) {
                const query: string = `${settingsContext.routes.messageUsers.base}${subscriber._id}/templates/${selectedTemplateId}`;

                return axios
                    .put(query, {
                        _id: selectedTemplateId,
                        name: templateName,
                        subscriberId: subscriber._id,
                        usernameReplacement: replacementUsername,
                        message: message,
                    })
                    .then(async response => {
                        if (response.status === 200) {
                            return response.data;
                        } else {
                            enqueueSnackbar('Error updating caption', {
                                variant: 'error',
                                autoHideDuration: 2000,
                            });
                        }
                    })
                    .catch(error => {
                        console.error(error);

                        enqueueSnackbar('Error updating caption', {
                            variant: 'error',
                            autoHideDuration: 2000,
                        });
                    });
            }
        };

        dialog
            .confirm({
                title: 'Update Capation',
                message: 'Are you sure you want to update this Caption?',
                ok: { text: 'Yes', color: 'primary', variant: 'contained' },
                cancel: { text: 'No', color: 'secondary', variant: 'outlined' },
            })
            .then(() => {
                updateTemplate().then((result: any) => {
                    if (result && result.success) {
                        enqueueSnackbar('Caption updated successfully', {
                            variant: 'success',
                            autoHideDuration: 2000,
                        });

                        setTemplateName('');
                        setReplacementUsername('');
                        setMessage('');
                        setIsEditMode(false);

                        refetchTemplates();
                    }
                });
            })
            .catch(() => {});
    };

    // Delete Subscriber's Template
    const handleDeleteSelectedTemplate = () => {
        if (!selectedTemplateId) return;

        const deleteTemplate = async (): Promise<any> => {
            if (subscriber && userContext.jwtToken && settingsContext.apiKey) {
                const query: string = `${settingsContext.routes.messageUsers.base}${subscriber._id}/templates/${selectedTemplateId}`;

                return axios
                    .delete(query)
                    .then(async response => {
                        if (response.status === 200) {
                            return response.data;
                        } else {
                            enqueueSnackbar('Error deleting caption', {
                                variant: 'error',
                                autoHideDuration: 2000,
                            });
                        }
                    })
                    .catch(error => {
                        console.error(error);

                        enqueueSnackbar('Error deleting caption', {
                            variant: 'error',
                            autoHideDuration: 2000,
                        });
                    });
            }
        };

        dialog
            .confirm({
                title: 'Delete Caption',
                message: 'Are you sure you want to delete this caption?',
                ok: { text: 'Yes', color: 'error', variant: 'contained' },
                cancel: { text: 'No', color: 'secondary', variant: 'outlined' },
            })
            .then(() => {
                deleteTemplate().then((result: any) => {
                    if (result && result.success) {
                        enqueueSnackbar('Caption deleted successfully', {
                            variant: 'success',
                            autoHideDuration: 2000,
                        });

                        setSelectedTemplateId('');
                        setSelectedTemplate(null);
                        setTemplateName('');
                        setReplacementUsername('');
                        setMessage('');
                        setIsEditMode(false);

                        refetchTemplates();
                    }
                });
            })
            .catch(() => {});
    };

    return (
        <DialogAdaptive maxWidth="sm" fullWidth open={loadTemplateDialogOpen} onClose={() => handleClose()}>
            <StyledCardTitleBar title="Load Message Caption" />

            <DialogContent
                sx={{
                    padding: theme.spacing(2),
                }}
            >
                <Stack direction="column" spacing={2}>
                    <DialogContentText>
                        <Typography variant="body1">Select a message caption to load.</Typography>

                        <Typography variant="body1">
                            <em>Note: This will overwrite the current message.</em>
                        </Typography>
                    </DialogContentText>

                    {isEditMode && (
                        <TextField
                            label="Template Name"
                            value={templateName || ''}
                            onChange={e => setTemplateName(e.currentTarget.value)}
                            fullWidth
                            error={isEditMode && templateName.trim().length === 0}
                        />
                    )}
                    {!isEditMode && (
                        <FormControl fullWidth>
                            <Select
                                fullWidth
                                autoFocus
                                displayEmpty
                                value={selectedTemplateId || ''}
                                // size="medium"
                                onChange={e => {
                                    setSelectedTemplateId(e.target.value);
                                }}
                            >
                                {(templatesLoading || templatesFetching) && (
                                    <MenuItem value={undefined}>
                                        <Grid container spacing={2} alignItems={'center'}>
                                            <Grid item xs>
                                                <Skeleton variant="text" width="100%" />
                                            </Grid>
                                        </Grid>
                                    </MenuItem>
                                )}
                                {!templatesLoading && !templatesFetching && templates && templates.length === 0 && (
                                    <MenuItem value={undefined} disabled>
                                        <Typography variant="body2" color="textSecondary">
                                            No captions found
                                        </Typography>
                                    </MenuItem>
                                )}
                                {!templatesLoading &&
                                    !templatesFetching &&
                                    templates &&
                                    templates.map((template: MessageUsersTemplate) => (
                                        <MenuItem key={template._id} value={template._id}>
                                            <Grid container spacing={2} alignItems={'center'}>
                                                <Grid item xs style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {template.name}
                                                </Grid>
                                                <Grid item xs="auto">
                                                    <Typography color="text.secondary" variant="body2">
                                                        {moment(objectIdToDate(template._id!)).format('L')}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    )}

                    <Divider />

                    {selectedTemplate && (
                        <Box>
                            <Grid container spacing={1} alignItems={'center'}>
                                <Grid item xs={12}>
                                    {isEditMode ? (
                                        <TextField
                                            label="Replacement Username"
                                            value={replacementUsername}
                                            onChange={e => setReplacementUsername(e.currentTarget.value)}
                                            disabled={!isEditMode}
                                            fullWidth
                                        />
                                    ) : (
                                        <Stack direction="column" spacing={1}>
                                            <Typography variant="body2" color="textSecondary">
                                                Replacement for %USERNAME%
                                            </Typography>
                                            <Typography variant="body1" color="textPrimary">
                                                {selectedTemplate.usernameReplacement}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary">
                                        Message
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    {isEditMode ? (
                                        <OnlyFansMessageEditorWithPreview
                                            subscriber={subscriber}
                                            message={message}
                                            setMessage={setMessage}
                                            messageHasRestrictedWords={false}
                                            messageFoundRestrictedWords={[]}
                                            checkForRestrictedWords={(message: string) => {}}
                                            taggedCreators={[]}
                                            taggedReleaseForms={[]}
                                            lockMessage={false}
                                            lockMessagePrice={0}
                                            lockedText={false}
                                            saveTemplate={false}
                                            setSaveTemplate={() => {}}
                                            saveTemplateName={''}
                                            setSaveTemplateName={() => {}}
                                            showUsernamePlaceholder={false}
                                            setUsernameReplacement={() => {}}
                                            showSaveTemplate={false}
                                            showPreview={false}
                                        />
                                    ) : (
                                        <MessagePreview message={wrapUsernamesWithLinks(selectedTemplate.message)} theme={theme} />
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider />
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            disabled={!selectedTemplateId}
                                            fullWidth
                                            onClick={() => {
                                                if (!isEditMode) {
                                                    setTemplateName(selectedTemplate?.name || '');
                                                    setReplacementUsername(selectedTemplate?.usernameReplacement || '');
                                                    setMessage(selectedTemplate?.message || '');
                                                }
                                                setIsEditMode(!isEditMode);
                                            }}
                                        >
                                            {isEditMode ? 'Cancel Edit' : 'Edit Caption'}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            disabled={isEditMode ? stripHtml(message.trim()).length === 0 : !selectedTemplateId}
                                            fullWidth
                                            onClick={() => {
                                                if (isEditMode) {
                                                    handleUpdateSelectedTemplate();
                                                } else {
                                                    handleDeleteSelectedTemplate();
                                                }
                                            }}
                                        >
                                            {isEditMode ? 'Save Changed' : 'Delete Caption'}
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.grey[100],
                }}
            >
                <Button onClick={handleClose} color="primary" size="medium">
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    size="medium"
                    loading={templatesLoading}
                    disabled={!templates || templates.length === 0 || !selectedTemplateId}
                >
                    <span>Load</span>
                </LoadingButton>
            </DialogActions>
        </DialogAdaptive>
    );
};

export default MessageUsersFormEditorTemplateLoadDialog;
